/**
 * HTTP-клиент к API.
 *
 * Единственное место, которое знает формат ошибок сервера
 * ({"error": {code, message, request_id}} — docs/06-api.md). Компоненты
 * работают с ApiError, а не с ответами fetch.
 */

export const API_BASE = '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string | undefined
  /** Секунды из заголовка Retry-After: по ним UI показывает обратный отсчёт. */
  readonly retryAfter: number | undefined

  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    retryAfter?: number,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.requestId = requestId
    this.retryAfter = retryAfter
  }
}

interface ErrorEnvelope {
  error?: {
    code?: unknown
    message?: unknown
    request_id?: unknown
  }
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = 'internal_error'
  let message = `Ошибка запроса (${response.status})`
  let requestId = response.headers.get('X-Request-Id') ?? undefined

  try {
    const body = (await response.json()) as ErrorEnvelope
    if (body.error) {
      code = asString(body.error.code, code)
      message = asString(body.error.message, message)
      requestId = asString(body.error.request_id, requestId ?? '') || undefined
    }
  } catch {
    // Тело не JSON (например, ответ прокси) — остаются значения по умолчанию.
  }

  const retryAfterHeader = Number(response.headers.get('Retry-After'))
  const retryAfter =
    Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader : undefined

  return new ApiError(response.status, code, message, requestId, retryAfter)
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    // Refresh-токен приходит в HttpOnly cookie (ADR-0005).
    credentials: 'same-origin',
  })

  if (!response.ok) {
    throw await toApiError(response)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}
